/**
 * The typed client for the daemon's own API.
 *
 * These types are hand-written rather than generated. The API is twenty endpoints on the same
 * side of the same repository, and a code generator would be a build step and a schema format
 * to maintain for the sake of a file this size. The cost is that they can drift from
 * `src/types.ts`; the containment is that both are in this repository and change together.
 */

import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser';

export type FixSource = 'ogn' | 'adsb';
export type FlightSource = 'ogn' | 'adsb' | 'mixed';
export type FlightStatus = 'open' | 'closed' | 'discarded';

export interface Fix {
  aircraftId: string;
  t: number;
  lat: number;
  lon: number;
  gnssAltM: number | null;
  pressAltM: number | null;
  groundSpeedKmh: number | null;
  trackDeg: number | null;
  climbMs: number | null;
  turnRate: number | null;
  accuracyM: number | null;
  precisionEnhanced: boolean;
  secondsSynthetic: boolean;
  source: FixSource;
  receiver: string | null;
  snrDb: number | null;
}

export interface TrackMetrics {
  fixCount: number;
  trackLengthKm: number;
  maxAltM: number | null;
  minAltM: number | null;
  maxClimbMs: number | null;
  gapCount: number;
  longestGapS: number;
  coverageRatio: number;
}

export interface Turnpoint {
  index: number;
  t: number;
  lat: number;
  lon: number;
  altM: number | null;
}

export interface Score {
  algoVersion: string;
  scoredAt: number;
  freeDistanceKm: number;
  freeDistanceUnconstrainedKm: number;
  freeTurnpoints: Turnpoint[];
  altitudeRuleOk: boolean;
  faiTriangleKm: number;
  faiValid: boolean;
  faiClosureM: number | null;
  faiTurnpoints: Turnpoint[];
  bestTriangleKm: number;
  bestTriangleValid: boolean;
  outAndReturnKm: number;
  outAndReturnTurnpoints: Turnpoint[];
  belowMinimumScore: boolean;
  olcClassicPoints: number;
  olcPlusPoints: number;
  olcPlusScore: number;
  weglideAreaBonusSource: 'fai' | 'out_and_return' | 'none';
  weglidePoints: number;
  weglideScore: number;
  straightKm: number;
  maxDistFromTakeoffKm: number;
  avgSpeedKmh: number;
  taskSpeedKmh: number;
  durationMs: number;
  notes: string | null;
}

export interface Flight {
  id: string;
  aircraftId: string;
  status: FlightStatus;
  takeoffT: number;
  landingT: number | null;
  durationS: number | null;
  takeoffLat: number | null;
  takeoffLon: number | null;
  takeoffAltM: number | null;
  landingLat: number | null;
  landingLon: number | null;
  landingAltM: number | null;
  takeoffPlace: string | null;
  takeoffPlaceCode: string | null;
  landingPlace: string | null;
  landingPlaceCode: string | null;
  source: FlightSource;
  landingInferred: boolean;
  metrics: TrackMetrics | null;
  badges?: BadgeAssessment | null;
  launch?: LaunchDetection | null;
  /** Mirrors the aircraft's flag, so the flight page does not need the fleet list to decide. */
  soaring?: boolean;
  igcPath: string | null;
  igcSha256: string | null;
  igcBytes: number | null;
  score: Score | null;
}

export interface LiveAircraft {
  aircraftId: string;
  airborne: boolean;
  since: number | null;
  flightId: string | null;
  lastFix: Fix | null;
  distanceFromTakeoffKm: number | null;
  lastFixAgeSeconds: number | null;
}

export interface Aircraft {
  id: string;
  registration: string;
  callsign: string | null;
  model: string | null;
  class: string | null;
  kind: string;
  /**
   * Whether this airframe's flights are soaring flights — glider or TMG, not an ultralight.
   *
   * Computed by the daemon (`soars()` in types.ts) rather than derived here, so there is one
   * place that decides and the two cannot drift apart. It gates every soaring-only number:
   * OLC and WeGlide, the badges, the best climb.
   */
  soaring: boolean;
  olcIndex: number;
  dmstIndex: number;
  pilot: string | null;
  devices: Array<{ address: string; addressType: number; label: string | null }>;
  live: LiveAircraft | null;
  flights: number;
}

export interface FlightPage {
  total: number;
  limit: number;
  offset: number;
  flights: Flight[];
}

export interface BadgeLeg {
  id: string;
  label: string;
  badge: 'silber' | 'gold' | 'diamant' | 'diplom';
  /** `met` = the track settles it. `indicative` = the performance is there and cannot be claimed. */
  status: 'met' | 'indicative' | 'not_met';
  value: number;
  required: number;
  unit: 'km' | 'm' | 'h';
  /** Why it can only ever be indicative, or null when the track genuinely settles it. */
  caveat: string | null;
}

export interface BadgeAssessment {
  rulesEdition: string;
  achieved: BadgeLeg[];
  all: BadgeLeg[];
  heightGainM: number;
  straightDistanceKm: number;
  maxFromLaunchKm: number;
  durationH: number;
}

export interface TerrainProfile {
  /** Ground elevation in metres, one per sample. */
  elevations: number[];
  /** Which track index each sample came from. */
  indices: number[];
  available: boolean;
}

export interface FlightWeather {
  cloudCover: number | null;
  cloudLow: number | null;
  cloudMid: number | null;
  cloudHigh: number | null;
  visibilityM: number | null;
  temperatureC: number | null;
  dewPointC: number | null;
  windSpeedKmh: number | null;
  windDirectionDeg: number | null;
  /** Which endpoint answered, so a surprising sky can be traced. */
  source: 'forecast' | 'archive' | 'unavailable';
  hourUtc: number;
}

export interface LaunchDetection {
  type: 'winch' | 'aerotow' | 'self_launch' | 'unknown';
  confidence: 'high' | 'medium' | 'low';
  /** Always populated, including for `unknown` — the reason is the useful part. */
  reason: string;
  climbMs: number;
  heightGainM: number;
  durationS: number;
  headingSpreadDeg: number;
}

/** One three-hour slot of the derived GAFOR-style outlook. See the daemon's `gafor.ts`. */
export interface GaforSlot {
  readonly fromT: number;
  readonly toT: number;
  /** `O`, `D`, `M`, `X`, or `?` when the model had nothing for that hour. */
  readonly category: 'O' | 'D' | 'M' | 'X' | '?';
  readonly visibilityM: number | null;
  readonly ceilingFt: number | null;
  readonly limitedBy: 'visibility' | 'ceiling' | 'both' | null;
}

export interface GaforOutlook {
  readonly position: { readonly lat: number; readonly lon: number; readonly t: number };
  /** Always true, and rendered: this is model data, not the DWD product. */
  readonly derived: boolean;
  readonly source: string;
  readonly note: string;
  readonly slots: readonly GaforSlot[];
}

/**
 * One NOTAM near the aircraft.
 *
 * The circle these describe is the NOTAM's *reference point and range* as published — a coarse
 * thing, not the shape of the affected airspace. See the daemon's `notam.ts`.
 */
export interface Notam {
  readonly number: string;
  readonly location: string | null;
  readonly text: string;
  readonly startT: number | null;
  readonly endT: number | null;
  readonly lat: number | null;
  readonly lon: number | null;
  readonly radiusNm: number | null;
  readonly lowerFl: number | null;
  readonly upperFl: number | null;
  readonly classification: string | null;
}

export interface NotamIndex {
  readonly notams: readonly Notam[];
  /** False when the daemon has no FAA credentials — *not* the same as "there are none". */
  readonly configured: boolean;
  readonly available: boolean;
  readonly centre: { readonly lat: number; readonly lon: number; readonly radiusNm: number };
}

/**
 * One place lift has been found before, from this club's own tracks.
 *
 * History, not a forecast — and silent about anywhere nobody has flown. See the daemon's
 * `thermalCells` for what counts as thermalling.
 */
export interface ThermalCell {
  readonly lat: number;
  readonly lon: number;
  /** Mean climb in the cell, m/s. */
  readonly climbMs: number;
  readonly bestMs: number;
  readonly samples: number;
  /** Mean altitude of the climbing fixes, metres MSL. */
  readonly altM: number;
}

export interface ThermalIndex {
  readonly cells: readonly ThermalCell[];
  readonly minTurnRate: number;
  readonly minClimbMs: number;
  readonly minSamples: number;
  readonly aircraft: readonly string[];
}

/** One precipitation radar frame. `forecast` marks a nowcast rather than an observation. */
export interface RadarFrame {
  readonly t: number;
  readonly forecast: boolean;
}

export interface RadarIndex {
  readonly frames: readonly RadarFrame[];
  readonly available: boolean;
}

export interface MapConfig {
  layers: Array<{
    id: string;
    attribution: string;
    needsKey: boolean;
    /** `[west, south, east, north]`, or null for a global layer. */
    bounds: [number, number, number, number] | null;
  }>;
  basemapStyleUrl: string;
  /** Glyph URL template for styles the client builds itself. Proxied, like everything else. */
  basemapGlyphsUrl: string;
  tileUrlTemplate: string;
  extraTileUrl: string | null;
  /** Package version, e.g. `0.1.0`. */
  version: string;
  /** The commit the image was built from, or `dev` from a checkout. */
  build: string;
}

export interface PeriodStats {
  period: string;
  aircraftId: string;
  flights: number;
  seconds: number;
  freeDistanceKm: number;
}

export interface TrackFeature {
  type: 'Feature';
  properties: {
    id: string;
    aircraftId: string;
    takeoffT: number;
    landingT: number | null;
    landingInferred: boolean;
    times: number[];
    altitudes: Array<number | null>;
    speeds: Array<number | null>;
  };
  geometry: { type: 'LineString'; coordinates: Array<[number, number, number]> };
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

/**
 * A JSON POST, sharing the error shape with `get`.
 *
 * `credentials: 'same-origin'` is the default and stated anyway: everything here depends on the
 * session cookie travelling, and a future change to the default would break authentication in a
 * way that looks like the server forgetting people.
 */
async function send<T>(method: 'POST' | 'DELETE', path: string, body?: unknown): Promise<T> {
  const response = await fetch(path, {
    method,
    credentials: 'same-origin',
    headers: {
      accept: 'application/json',
      ...(body === undefined ? {} : { 'content-type': 'application/json' }),
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });

  if (!response.ok) {
    let code = `http_${response.status}`;
    let message = response.statusText;
    try {
      const parsed = (await response.json()) as { error?: { code?: string; message?: string } };
      code = parsed.error?.code ?? code;
      message = parsed.error?.message ?? message;
    } catch {
      // Not JSON. The status line is all there is.
    }
    throw new ApiError(response.status, code, message);
  }

  return (await response.json()) as T;
}

const post = <T>(path: string, body: unknown): Promise<T> => send<T>('POST', path, body);
const del = <T>(path: string): Promise<T> => send<T>('DELETE', path);

async function get<T>(path: string, signal?: AbortSignal): Promise<T> {
  const response = await fetch(path, { signal, headers: { accept: 'application/json' } });

  if (!response.ok) {
    // The daemon answers `{ error: { code, message } }` for everything it refuses, so the
    // message shown to a user is the daemon's own rather than "Request failed with 503".
    let code = `http_${response.status}`;
    let message = response.statusText;
    try {
      const body = (await response.json()) as { error?: { code?: string; message?: string } };
      code = body.error?.code ?? code;
      message = body.error?.message ?? message;
    } catch {
      // Not JSON. The status line is all there is.
    }
    throw new ApiError(response.status, code, message);
  }

  return (await response.json()) as T;
}

/**
 * Estimated cloud base and cover for one hour of a flight.
 *
 * The base is a guess from the temperature/dew-point spread, not a measurement, and it errs
 * optimistic. See the daemon's `cloudsForFlight`.
 */
export interface CloudHour {
  readonly t: number;
  /** Metres above sea level, or null when the model had no spread for that hour. */
  readonly baseM: number | null;
  readonly cover: number | null;
  readonly coverLow: number | null;
  readonly coverMid: number | null;
}

export interface CloudProfile {
  readonly hours: readonly CloudHour[];
  readonly groundM: number;
  readonly available: boolean;
  readonly estimated?: boolean;
  readonly note?: string;
}

/**
 * What the shell needs before it can decide whether to show the logbook or a gate.
 *
 * `claimed` and `signedIn` are separate on purpose: an unclaimed instance shows a setup form, a
 * claimed one shows a login, and conflating them would put the setup form in front of anybody
 * who happened to be signed out.
 */
export interface AuthState {
  readonly claimed: boolean;
  readonly signedIn: boolean;
  readonly username: string | null;
  readonly claimNeedsToken: boolean;
  readonly claimPossible: boolean;
}

/**
 * One registered passkey.
 *
 * `backedUp` is the interesting field: false means the credential lives in one piece of
 * hardware and dies with it — a Windows Hello key on a desktop, a security stick — and cannot
 * appear on another device however you sign in there. That is the whole reason this list is
 * shown rather than hidden in the API.
 */
export interface Passkey {
  readonly id: string;
  readonly label: string | null;
  readonly aaguid: string | null;
  readonly backedUp: boolean;
  readonly createdAt: number;
  readonly lastUsedAt: number | null;
}

export interface HeatmapParams {
  /** Aircraft id, or empty/undefined for the whole fleet. */
  readonly aircraft?: string | undefined;
  /** `YYYY-MM-DD`, inclusive. */
  readonly from?: string | undefined;
  /** `YYYY-MM-DD`, inclusive — the server turns it into an exclusive bound a day later. */
  readonly to?: string | undefined;
  /** Grid size in degrees. */
  readonly cell?: number | undefined;
}

export interface Heatmap {
  cellDeg: number;
  flights: number;
  fixes: number;
  maxCount: number;
  /** The 98th percentile — what the heat scale should use. See `heatmapCells`. */
  p98Count: number;
  /**
   * The box holding almost all of the recorded time — what the map should open on.
   *
   * `bounds` is the extreme box, and one 600 km ferry leg is enough to make it useless. See the
   * daemon's `focusBox`.
   */
  focus: { minLat: number; minLon: number; maxLat: number; maxLon: number } | null;
  bounds: { minLat: number; minLon: number; maxLat: number; maxLon: number } | null;
  /** `[lat, lon, count]` at each cell's centre. */
  cells: Array<[number, number, number]>;
}

export const api = {
  aircraft: (signal?: AbortSignal) => get<Aircraft[]>('/api/v1/aircraft', signal),

  live: (signal?: AbortSignal) => get<LiveAircraft[]>('/api/v1/live', signal),

  flights: (
    params: {
      aircraft?: string;
      /** `YYYY-MM-DD`, inclusive at both ends — the server advances `to` by a day itself. */
      from?: string;
      to?: string;
      limit?: number;
      offset?: number;
      status?: string;
    } = {},
    signal?: AbortSignal,
  ) => {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== '') query.set(key, String(value));
    }
    const suffix = query.size > 0 ? `?${query}` : '';
    return get<FlightPage>(`/api/v1/flights${suffix}`, signal);
  },

  flight: (id: string, signal?: AbortSignal) =>
    get<Flight>(`/api/v1/flights/${encodeURIComponent(id)}`, signal),

  track: (id: string, signal?: AbortSignal) =>
    get<TrackFeature>(`/api/v1/flights/${encodeURIComponent(id)}/track.geojson`, signal),

  stats: (by: 'year' | 'month' = 'year', signal?: AbortSignal) =>
    get<{ granularity: string; periods: PeriodStats[] }>(`/api/v1/stats?by=${by}`, signal),

  mapConfig: (signal?: AbortSignal) => get<MapConfig>('/api/v1/map/config', signal),

  terrain: (id: string, signal?: AbortSignal) =>
    get<TerrainProfile>(`/api/v1/flights/${encodeURIComponent(id)}/terrain`, signal),

  weather: (id: string, signal?: AbortSignal) =>
    get<FlightWeather>(`/api/v1/flights/${encodeURIComponent(id)}/weather`, signal),

  authState: (signal?: AbortSignal) => get<AuthState>('/api/v1/auth/state', signal),

  registerOptions: (username: string, claimToken: string) =>
    post<{ flowId: string; options: PublicKeyCredentialCreationOptionsJSON }>(
      '/api/v1/auth/register/options',
      { username, claimToken },
    ),

  registerVerify: (flowId: string, response: unknown) =>
    post<{ username: string }>('/api/v1/auth/register/verify', { flowId, response }),

  loginOptions: () =>
    post<{ flowId: string; options: PublicKeyCredentialRequestOptionsJSON }>(
      '/api/v1/auth/login/options',
      {},
    ),

  loginVerify: (flowId: string, response: unknown) =>
    post<{ username: string }>('/api/v1/auth/login/verify', { flowId, response }),

  logout: () => post<{ ok: boolean }>('/api/v1/auth/logout', {}),

  /**
   * Add a second passkey to the account that already exists.
   *
   * Not registration: this needs the session, not the claim token, and it is the only way onto
   * a phone when the first key was a device-bound one. Without it a second device is
   * unreachable and a lost first device means editing SQLite by hand.
   */
  credentials: (signal?: AbortSignal) => get<Passkey[]>('/api/v1/auth/credentials', signal),

  credentialsOptions: () =>
    post<{ flowId: string; options: PublicKeyCredentialCreationOptionsJSON }>(
      '/api/v1/auth/credentials/options',
      {},
    ),

  credentialsVerify: (flowId: string, response: unknown, label: string | null) =>
    post<{ id: string }>('/api/v1/auth/credentials/verify', { flowId, response, label }),

  removeCredential: (id: string) =>
    del<{ removed: number }>(`/api/v1/auth/credentials/${encodeURIComponent(id)}`),

  revokeSessions: () => post<{ revoked: number }>('/api/v1/auth/sessions/revoke', {}),

  clouds: (id: string, signal?: AbortSignal) =>
    get<CloudProfile>(`/api/v1/flights/${encodeURIComponent(id)}/clouds`, signal),

  notams: (id: string, signal?: AbortSignal) =>
    get<NotamIndex>(`/api/v1/flights/${encodeURIComponent(id)}/notams`, signal),

  thermals: (bbox: [number, number, number, number], signal?: AbortSignal) =>
    get<ThermalIndex>(`/api/v1/thermals?bbox=${bbox.map((v) => v.toFixed(4)).join(',')}`, signal),

  radarFrames: (signal?: AbortSignal) => get<RadarIndex>('/api/v1/radar/frames', signal),

  /** The tile template for one radar frame, for a MapLibre raster source. */
  radarTileUrl: (frameT: number) => `/api/v1/radar/${frameT}/{z}/{x}/{y}`,

  gafor: (id: string, signal?: AbortSignal) =>
    get<GaforOutlook>(`/api/v1/flights/${encodeURIComponent(id)}/gafor`, signal),

  heatmap: (params: HeatmapParams, signal?: AbortSignal) => {
    const query = new URLSearchParams();
    if (params.aircraft !== undefined && params.aircraft !== '') query.set('aircraft', params.aircraft);
    if (params.from !== undefined && params.from !== '') query.set('from', params.from);
    if (params.to !== undefined && params.to !== '') query.set('to', params.to);
    if (params.cell !== undefined) query.set('cell', String(params.cell));
    const suffix = query.size > 0 ? `?${query}` : '';
    return get<Heatmap>(`/api/v1/heatmap${suffix}`, signal);
  },

  igcUrl: (id: string) => `/api/v1/flights/${encodeURIComponent(id)}/igc`,

  csvUrl: (params: { aircraft?: string; from?: string; to?: string } = {}) => {
    // The export follows the filters on screen. A CSV that silently ignored the date range
    // while the list obeyed it would be the more surprising of the two.
    const query = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== '') query.set(k, v);
    return query.size > 0 ? `/api/v1/logbook.csv?${query}` : '/api/v1/logbook.csv';
  },
};
